import { useDrawer } from '@iveco-ti/iveco-ds'
import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { CheckCircle, ServerCrash } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { Toast } from '@/common/components/Toast'
import { useModalProvider } from '@/common/hooks/useModalProvider'
import { api, ApiError } from '@/lib/axios'
import { queryClient } from '@/lib/tanstack'

async function deleteApplicationAccount(id: string) {
  return api.backend.delete(`/application-account/${id}`)
}

export function useInactivateApplicationAccount() {
  const { t } = useTranslation('sso')
  const { handleModal, toggleLoading } = useModalProvider()
  const drawerManageApplications = useDrawer('drawer-manage-applications')

  const { mutateAsync, isPending } = useMutation<
    unknown,
    AxiosError<ApiError>,
    string
  >({
    mutationFn: deleteApplicationAccount,
    onSuccess: () => {
      toast(Toast, {
        data: {
          type: 'success',
          icon: CheckCircle,
          title: t(
            'accounts.use_inactivate_application_account.toast_success.title',
          ),
        },
      })
      queryClient.invalidateQueries({ queryKey: ['/account/paginate'] })
    },
    onError: (error) => {
      toast(Toast, {
        data: {
          type: 'danger',
          icon: ServerCrash,
          title: t(
            'accounts.use_inactivate_application_account.toast_error.title',
          ),
          description:
            error?.response?.data?.friendlyMessage ||
            t(
              'accounts.use_inactivate_application_account.toast_error.description',
            ),
        },
      })
    },
  })

  const handleInactivateApplicationAccount = async (id: string) => {
    handleModal({
      type: 'danger',
      title: t('accounts.use_inactivate_application_account.modal.title'),
      description: t(
        'accounts.use_inactivate_application_account.modal.description',
      ),
      confirmText: t(
        'accounts.use_inactivate_application_account.modal.button_confirm',
      ),
      onCancel: () => drawerManageApplications.open(),
      onConfirm: async () => {
        toggleLoading()
        await mutateAsync(id).finally(() => {
          toggleLoading()
          drawerManageApplications.open()
        })
      },
    })
  }

  return {
    handleInactivateApplicationAccount,
    isInactivatingApplicationAccount: isPending,
  }
}
